import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HomeSnapshot } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { Icon } from "@/shared/ui";
import "./realSite.css";

/** 告知、预览挂在 queryKeys.homePlace(petId) 下面：确认、选城后让这个前缀失效，读住处、告知、预览一起重读。 */
export const realSiteNoticeKey = (petId: string) => [...queryKeys.homePlace(petId), "real-site-notice"] as const;
export const realSitePreviewKey = (petId: string) => [...queryKeys.homePlace(petId), "real-site-preview"] as const;
export const realSiteHref = (petId: string) => `/home/real-site?pet=${encodeURIComponent(petId)}`;

/**
 * 老家补真实住处的一次性告知（UI2-HOME-SMALL-01；参考稿 ASSET-UI-030 C4，DOM 实现，插画等 I 出素材前用现有图标）。
 * 只在后端说该出（GET /home/place/real-site/notice 的 show）时出；点「看看」或「以后再说」都记一次（POST，家里谁关都算），之后不再出。
 * 记过以后，场景下面那一行「家在……」里的「看看住处」一直在，随时能进预览页。
 * 压在场景底部那一截：TA 的可走区最前只到底图约 72% 处（homeStage FLOOR），「看 TA」「庭院」都在它上面，这张卡压不到 TA 和这些按钮。
 * 演示模式不出（演示里不换住处）。
 */
/**
 * `otherwise`：卡不出（后端说不该出、已经记过、还在读）时，在原位放的东西（UI2-REVIEW110-01：场景底部的告示栏里放「家在……」那一行）。
 * 卡和那一行说的是同一件事、点进去是同一页；卡在的时候只放卡，免得 320 宽时告示栏顶到「走回庭院」。
 */
export function RealSiteNotice({ petId, otherwise = null }: { petId: string; otherwise?: ReactNode }) {
  const { homePlace } = useServices();
  const client = useQueryClient();
  const [closed, setClosed] = useState(false);
  const notice = useQuery({
    queryKey: realSiteNoticeKey(petId),
    queryFn: ({ signal }) => homePlace.realSiteNotice(petId, signal),
    enabled: env.dataMode === "live",
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const seen = useMutation({
    mutationFn: () => homePlace.realSiteNoticeSeen(petId),
    onSuccess: (next) => client.setQueryData(realSiteNoticeKey(petId), next),
  });
  if (closed || !notice.data?.show) return <>{otherwise}</>;
  // 记不上（断网）也先收起：下次进小窝后端还说该出，就再出一次，不会丢
  const record = () => { setClosed(true); seen.mutate(); };
  return (
    <aside className="ps-home-place-notice" aria-labelledby="ps-home-place-notice-title" data-testid="home-real-site-notice">
      <span className="ps-home-place-notice__icon" aria-hidden="true"><Icon name="home" size={26} /></span>
      <div className="ps-home-place-notice__body">
        <h2 id="ps-home-place-notice-title">给 TA 找一个真实的住处</h2>
        <p>让 TA 的家落在城里一个真实的小区，出门就从那里出发。</p>
        <div className="ps-home-place-notice__actions">
          <Link className="ps-btn ps-btn--primary ps-btn--sm" to={realSiteHref(petId)} onClick={record}>看看</Link>
          <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={record}>以后再说</button>
        </div>
      </div>
    </aside>
  );
}

/**
 * 场景下面一行：家在哪，加一个随时能进预览页的「看看住处」（一次性告知点过「以后再说」以后，从这里还能进）。
 * 说法照后端 display：选过城是「城市·片区」，有真实小区时用只给本家人看的 private_display；没选过城后端写的是「暂住…（还没选城市）」。
 * 地址还在落定时不给入口（上面那句「新家地址还在落定……」已经说了），演示模式也不给。
 */
export function HomePlaceLine({ snapshot }: { snapshot: HomeSnapshot }) {
  const place = snapshot.place;
  if (!place) return null;
  const where = place.private_display ?? place.display;
  const canLook = env.dataMode === "live" && !place.address_pending;
  return (
    <p className="ps-home-place-line" data-testid="home-place-line">
      <Icon name="home" size={14} />
      <span>{place.chosen ? `家在 ${where}` : `家${where}`}</span>
      {canLook ? <Link to={realSiteHref(snapshot.pet.pet_id)}>看看住处</Link> : null}
    </p>
  );
}
